import { evaluateMotionElement } from "./evaluateMotionElement";
import type { MotionPreset } from "./types";

// Caption cues and words animate with the same enter/exit presets as every
// motion element, evaluated on their own spans: a cue from its first word to
// its last word's end, a word from when it is spoken to the end of its cue.

export interface CaptionMotionState {
  opacity: number;
  x: number;
  y: number;
  scale: number;
  blur: number;
  rotateY: number;
  skewX: number;
  perspective?: number;
  /** Wipe insets, in % of the word/cue box. */
  clip?: { top: number; right: number; bottom: number; left: number };
}

/** The preset state at `time` for the span [start, end): hidden outside it. */
export function evaluateCaptionPreset(
  { start, end, enter, exit }: { start: number; end: number; enter?: MotionPreset; exit?: MotionPreset },
  time: number,
): CaptionMotionState {
  // A 100×100 box makes wipe insets percentages.
  const evaluated = evaluateMotionElement(
    {
      id: "caption",
      type: "rect",
      start,
      end,
      width: 100,
      height: 100,
      ...(enter ? { enter } : {}),
      ...(exit ? { exit } : {}),
    },
    time,
  );
  const clip = typeof evaluated.clip === "object" ? evaluated.clip : undefined;
  return {
    opacity: evaluated.opacity ?? 1,
    x: evaluated.x ?? 0,
    y: evaluated.y ?? 0,
    scale: evaluated.scale ?? 1,
    blur: evaluated.blur ?? 0,
    rotateY: evaluated.rotateY ?? 0,
    skewX: evaluated.skewX ?? 0,
    ...(evaluated.perspective ? { perspective: evaluated.perspective } : {}),
    ...(clip
      ? {
          clip: {
            top: clip.top ?? 0,
            right: clip.right ?? 0,
            bottom: clip.bottom ?? 0,
            left: clip.left ?? 0,
          },
        }
      : {}),
  };
}

/** Caption words never break inside a word: a line whose longest word cannot
 *  fit the caption width gets a smaller size. The width is estimated from the
 *  character count (wide heavy capitals ≈ 0.74 em), so preview and export
 *  agree without measuring fonts. */
export function fitCaptionFontSize({
  words,
  size,
  maxWidth,
  uppercase,
  weight = 400,
  letterSpacing = 0,
  strokeWidth = 0,
  wordScale = 1,
  wordPadding = 0,
}: {
  words: readonly string[];
  size: number;
  maxWidth: number;
  uppercase: boolean;
  weight?: number;
  letterSpacing?: number;
  strokeWidth?: number;
  /** How much a spoken word grows (activeWord scale, highlight padding). */
  wordScale?: number;
  wordPadding?: number;
}): number {
  const longest = Math.max(0, ...words.map((word) => Array.from(word.trim()).length));
  if (!longest || maxWidth <= 0) return size;
  const factor = uppercase ? (weight >= 700 ? 0.74 : 0.68) : weight >= 700 ? 0.6 : 0.54;
  const edges = (strokeWidth + wordPadding) * 2;
  const estimate = longest * (size * factor + letterSpacing) * wordScale + edges;
  if (estimate <= maxWidth) return size;
  const fitted = ((maxWidth - edges) / wordScale / longest - letterSpacing) / factor;
  return Math.max(8, Math.floor(Math.min(size, fitted) * 100) / 100);
}
